import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useFontSize } from './FontSizeContext';
import { Save, ArrowLeft, Key, Mail, ShieldCheck, MapPin, User, Building2, Users, CalendarClock, Trash2 } from 'lucide-react';
import AuditHistoryModal from './AuditHistoryModal';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';

const InputField = ({ label, value, onChange, type = "text", disabled = false, icon: Icon, autoComplete = "off" }) => (
  <div className="flex flex-col border-b border-line-subtle py-1">
    <label className="text-[0.9em] text-ink-secondary italic font-black uppercase mb-1">{label}</label>
    <div className="flex items-center gap-2">
      {Icon && <Icon size={14} className="text-line" />}
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        autoComplete={autoComplete}
        className="w-full outline-none font-black text-primary text-[12px] bg-transparent uppercase placeholder:italic placeholder:font-normal disabled:opacity-50"
        placeholder={`ENTER ${label}...`}
      />
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-surface-card border-2 border-line mb-6 rounded-sm shadow-sm">
    <div className="bg-surface-panel p-2 border-b border-line font-black text-primary italic uppercase text-[1.0em] tracking-widest">{title}</div>
    <div className="p-6 grid grid-cols-2 gap-x-12 gap-y-4">{children}</div>
  </div>
);

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const isNew = !id || id === 'new' || id === 'undefined';
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const isSelf = !isNew && String(session.user_id) === String(id);

  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [sites, setSites] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // 🎯 لملء قائمة التفويض
  const [showAudit, setShowAudit] = useState(false);
  // 🎯 حقل الباسورد لليوزر الموجود يفضل مقفول لحد ما الأدمن يضغط "Reset
  // Password" صراحة - غير كده متصفحات زي Chrome بتحاول تعمل Autofill لحقل
  // password فاضي بباسورد محفوظ للموقع (مش باسورد اليوزر ده أصلاً)، ولو
  // الأدمن مضغطش لاحظ ده وحفظ عادي، بيبدّل باسورد اليوزر بحاجة عشوائية
  // من غير قصد. اليوزر الجديد (isNew) مالوش باسورد قديم يتضيّع، فمحتاجش القفل ده
  const [resetPassword, setResetPassword] = useState(false);

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [userData, setUserData] = useState({
    username: '', full_name: '', email: '', password: '',
    role_id: '', site_id: '', org_id: '', is_active: true, language: 'EN',
    additional_roles: [], // 🎯 رولات إضافية
    delegation: { delegate_user_id: '', start_date: '', end_date: '' } // 🎯 بيانات التفويض
  });

  const fetchOrgs = async () => {
      const res = await axios.get('/api/organizations');
      setOrgs(res.data);
  };

  useEffect(() => {
      fetchOrgs();
      if (userData.org_id) {
          axios.get(`/api/sites/${userData.org_id}`)
              .then(res => {
                  const data = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
                  setSites(data);
              });
      }
  }, [userData.org_id]);

  // 🎯 قائمة اليوزرز المرشحين للتفويض لازم تتقفل على نفس مؤسسة/موقع اليوزر
  // اللي بنعدله - قبل كده كانت بتجيب كل يوزرز النظام من غير أي فلترة، يعني
  // ممكن تفوض حد من مؤسسة تانية خالص. بتتجدد كل ما org_id/site_id يتغيروا
  useEffect(() => {
      if (!userData.org_id) { setAllUsers([]); return; }
      const token = localStorage.getItem('token');
      const params = { limit: 500, org_id: userData.org_id };
      if (userData.site_id) params.site_id = userData.site_id;
      axios.get('/api/users', { headers: { 'Authorization': `Bearer ${token}` }, params })
          .then(res => setAllUsers(res.data.data || []))
          .catch(() => setAllUsers([]));
  }, [userData.org_id, userData.site_id]);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
  
      try {
        const rolesRes = await axios.get('/api/roles', { headers });
        setRoles(rolesRes.data);

        if (isNew) {
          setUserData(prev => ({ ...prev }));
          return; 
        }
  
        setLoading(true);
        const userRes = await axios.get(`/api/users/${id}`, { headers });
        setUserData({ 
            ...userRes.data, 
            password: '',
            additional_roles: userRes.data.additional_roles || [],
            delegation: userRes.data.delegation || { delegate_user_id: '', start_date: '', end_date: '' }
        });
      } catch (err) {
        console.error("Fetch Error:", err);
        if (!isNew) toast.error("User not found");
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [id, isNew]);

  const handleSave = async () => {
    if (!userData.org_id || !userData.site_id) {
      return toast.error("Organization and Site are required for every user");
    }
    if (!userData.role_id) {
      return toast.error("A primary role is required for every user");
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // تنظيف بيانات التفويض لو مش كاملة
      let payload = { ...userData };
      if (!payload.delegation.delegate_user_id || !payload.delegation.start_date || !payload.delegation.end_date) {
          payload.delegation = null;
      }

      // 🎯 نبعت الباسورد بس لو (يوزر جديد) أو (الأدمن ضغط Reset Password
      // صراحة) - أي قيمة تانية جاية من Autofill المتصفح من غير قصد اتشالت
      if (!isNew && !resetPassword) {
          delete payload.password;
      }

      if (isNew) {
        await axios.post('/api/users', payload, { headers });
        toast.success("User created successfully");
      } else {
        await axios.put(`/api/users/${id}`, payload, { headers });
        toast.success("User updated successfully");
      }
      navigate('/users');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error saving user");
    } finally { setLoading(false); }
  };

  const handleDelete = () => {
    if (isSelf) return toast.error("You cannot delete your own account while logged in.");
    Swal.fire({
      ...swalTheme(),
      title: "Are you sure?", text: "This action cannot be undone!",
      icon: 'warning', showCancelButton: true, confirmButtonColor: swalDangerColor(),
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`/api/users/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
          toast.success("User deleted successfully");
          navigate('/users');
        } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
      }
    });
  };

  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);

  // دالة للتحكم في تحديد وإلغاء الرولات الإضافية
  const toggleAdditionalRole = (roleId) => {
      setUserData(prev => {
          const currentRoles = prev.additional_roles;
          if (currentRoles.includes(roleId)) {
              return { ...prev, additional_roles: currentRoles.filter(id => id !== roleId) };
          } else {
              return { ...prev, additional_roles: [...currentRoles, roleId] };
          }
      });
  };

  return (
    <div className="flex flex-col h-full bg-surface-app font-bold uppercase tracking-tighter" style={{ fontSize: `${fontSize}px` }}>
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="users" recordId={id} />
      {/* Header Bar */}
      <div className="bg-surface-card p-3 border-b border-line shadow-sm flex justify-between items-center sticky top-0 z-20">
        <div className="flex items-center gap-4 ml-4">
          <button onClick={() => navigate('/users')} className="p-1 hover:bg-surface-panel rounded-full transition-all text-primary" >
            <ArrowLeft size={18} { ...(isRTL && { className: 'rotate-180' }) } />
          </button>
          <div className="flex flex-col">
            <span className="text-[9px] text-ink-secondary leading-none italic">{t('userDetails.titleEdit')}</span>
            <span className="text-primary font-black text-lg leading-none">{userData.username || t('userDetails.titleNew')}</span>
          </div>
        </div>
        {!isNew && !isSelf && (
          <button
            onClick={handleDelete}
            className="p-2.5 text-status-danger hover:bg-status-danger-bg rounded-sm transition-colors mr-2"
            title="Delete User"
          >
            <Trash2 size={16}/>
          </button>
        )}
        <button
          onClick={handleSave} disabled={loading}
          className="bg-primary text-white px-10 py-2.5 rounded-sm flex items-center gap-2 hover:bg-primary-dark transition-all mr-4 shadow-lg active:scale-95"
        >
          <Save size={16} /> {loading ? t('userDetails.saving') : t('userDetails.btnSave')}
        </button>
      </div>

      <div className="flex-1 overflow-auto p-8 max-w-5xl mx-auto w-full">
        {/* 1. Identification Section */}
        <Section title={t('userDetails.secIdent')}>
          <InputField label={t('userDetails.lblLoginId')} value={userData.username} onChange={v => setUserData({...userData, username: v})} disabled={!isNew} icon={User} autoComplete="off" />
          <InputField label={t('userDetails.lblFullName')} value={userData.full_name} onChange={v => setUserData({...userData, full_name: v})} />
          <InputField label={t('userDetails.lblDisplayName')} value={userData.display_name} onChange={v => setUserData({...userData, display_name: v})} />
          <InputField label={t('userDetails.lblEmail')} value={userData.email} onChange={v => setUserData({...userData, email: v.toLowerCase()})} icon={Mail} type="email" autoComplete="off" />

          {isNew ? (
            <InputField label={t('userDetails.lblPassword')} value={userData.password} onChange={v => setUserData({...userData, password: v})} type="password" icon={Key} autoComplete="new-password" />
          ) : (
            <div className="flex flex-col border-b border-line-subtle py-1">
              <div className="flex items-center justify-between mb-1">
                <label className="text-[0.9em] text-ink-secondary italic font-black uppercase">{t('userDetails.lblPassword')}</label>
                <label className="flex items-center gap-1.5 cursor-pointer text-[0.85em] text-status-warning font-black uppercase">
                  <input
                    type="checkbox"
                    checked={resetPassword}
                    onChange={e => { setResetPassword(e.target.checked); if (!e.target.checked) setUserData(prev => ({...prev, password: ''})); }}
                    className="w-3.5 h-3.5 cursor-pointer accent-primary"
                  />
                  {t('userDetails.chkResetPassword')}
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Key size={14} className="text-line" />
                <input
                  type="password"
                  value={userData.password || ''}
                  onChange={e => setUserData({...userData, password: e.target.value})}
                  disabled={!resetPassword}
                  autoComplete="new-password"
                  className="w-full outline-none font-black text-primary text-[12px] bg-transparent uppercase placeholder:italic placeholder:font-normal disabled:opacity-50"
                  placeholder={resetPassword ? t('userDetails.phNewPassword') : t('userDetails.phPasswordLocked')}
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 py-2 border-b border-line-subtle">
             <label className="text-[9px] text-ink-secondary italic font-black uppercase">{t('userDetails.lblActive')}</label>
             <input type="checkbox" checked={userData.is_active} onChange={e => setUserData({...userData, is_active: e.target.checked})} className="w-4 h-4 cursor-pointer accent-primary" />
          </div>
        </Section>

        {/* 2. Access Control Section */}
        <Section title={t('userDetails.secAccess')}>
          <div className="flex flex-col border-b border-line-subtle py-1">
            <label className="text-[9px] text-ink-secondary italic font-black uppercase mb-1">{t('userDetails.lblPrimaryRole')} <span className="text-status-danger">*</span></label>
            <div className="flex items-center gap-2">
              <ShieldCheck size={14} className="text-status-warning" />
              <select
                value={userData.role_id}
                onChange={e => setUserData({...userData, role_id: e.target.value})}
                className="w-full bg-transparent outline-none font-black text-primary text-[12px] uppercase cursor-pointer"
              >
                <option value="">-- SELECT PRIMARY ROLE --</option>
                {roles.map(r => <option key={r.role_id} value={r.role_id}>{r.name}</option>)}
              </select>
            </div>
          </div>

          <div className="flex flex-col border-b border-line group">
              <label className="text-[9px] font-black text-primary uppercase tracking-tighter flex items-center gap-1 mb-1">
                  <Building2 size={10}/> {t('userDetails.lblDefOrg')} <span className="text-status-danger">*</span>
              </label>
              <select
                  className="outline-none py-1 font-black text-primary bg-transparent uppercase text-[11px]"
                  value={userData.org_id}
                  onChange={e => setUserData({...userData, org_id: e.target.value, site_id: ''})}
              >
                  <option value="">Select Org...</option>
                  {orgs.map(org => <option key={org.org_id} value={org.org_id}>{org.description}</option>)}
              </select>
          </div>

          <div className="flex flex-col border-b border-line group">
              <label className="text-[0.9em] font-black text-primary uppercase tracking-tighter flex items-center gap-1 mb-1">
                  <MapPin size={10}/> {t('userDetails.lblDefSite')} <span className="text-status-danger">*</span>
              </label>
              <select
                  className="outline-none py-1 font-black text-primary bg-transparent uppercase text-[11px]"
                  value={userData.site_id}
                  onChange={e => setUserData({...userData, site_id: e.target.value})}
              >
                  <option value="">Select Site...</option>
                  {sites.map(site => <option key={site.site_id} value={site.site_id}>{site.description}</option>)}
              </select>
          </div>

          {/* 🎯 Secondary Roles (Multi-Select via Checkboxes) */}
          <div className="col-span-2 mt-4">
              <label className="text-[0.9em] font-black text-ink-secondary uppercase tracking-tighter flex items-center gap-1 mb-2">
                  <Users size={12} className="text-ink-secondary"/> {t('userDetails.lblSecRoles')}
              </label>
              <div className="grid grid-cols-3 gap-2 bg-surface-panel p-3 rounded border border-line max-h-32 overflow-y-auto">
                  {roles.filter(r => String(r.role_id) !== String(userData.role_id ?? '')).map(role => (
                      <label key={role.role_id} className="flex items-center gap-2 cursor-pointer text-[1.0em] text-ink hover:text-primary">
                          <input
                              type="checkbox"
                              checked={userData.additional_roles.includes(role.role_id)}
                              onChange={() => toggleAdditionalRole(role.role_id)}
                              className="accent-primary"
                          />
                          {role.name}
                      </label>
                  ))}
              </div>
          </div>
        </Section>

        {/* 3. Delegation Section 🎯 */}
        <Section title={t('userDetails.secDelegation')}>
          <div className="flex flex-col border-b border-line-subtle py-1">
            <label className="text-[0.9em] text-ink-secondary italic font-black uppercase mb-1">{t('userDetails.lblDelegateTo')}</label>
            <div className="flex items-center gap-2">
              <User size={14} className="text-accent" />
              <select
                value={userData.delegation.delegate_user_id}
                onChange={e => setUserData({...userData, delegation: {...userData.delegation, delegate_user_id: e.target.value}})}
                className="w-full bg-transparent outline-none font-black text-primary text-[12px] uppercase cursor-pointer"
              >
                <option value="">-- NO DELEGATION --</option>
                {allUsers.filter(u => u.user_id !== parseInt(id)).map(u => (
                    <option key={u.user_id} value={u.user_id}>{u.username} - {u.full_name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="col-span-2 grid grid-cols-2 gap-12 mt-2">
              <InputField 
                  label={t('userDetails.lblStartDate')} 
                  type="datetime-local" 
                  value={userData.delegation.start_date} 
                  onChange={v => setUserData({...userData, delegation: {...userData.delegation, start_date: v}})} 
                  icon={CalendarClock}
                  disabled={!userData.delegation.delegate_user_id}
              />
              <InputField 
                  label={t('userDetails.lblEndDate')} 
                  type="datetime-local" 
                  value={userData.delegation.end_date} 
                  onChange={v => setUserData({...userData, delegation: {...userData.delegation, end_date: v}})} 
                  icon={CalendarClock}
                  disabled={!userData.delegation.delegate_user_id}
              />
          </div>
        </Section>
      </div>
    </div>
  );
};

export default UserDetails;